//#region imports
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';

// redux
import { useDispatch, useSelector } from 'react-redux';
import {
    selectSelectedReportType,
    setSelectedReportType,
    selectIncludeNoShows,
    getPassengerRecordsReport,
    selectPassengerRecordsReportDataAndColumns,
    getPassengerBreakdown,
    selectPassengerBreakdownStatus,
    selectPassengerBreakdownDataAndColumns,
    selectSelectedDepartureArrival,
    selectPassengerRecordsReportStatus,
    selectShowPaxJourney,
    setShowPaxJourney
} from '_redux/slices/timeline-reports/timeline-reports';

import {
    selectSLAReportStatus,
    selectExternalKPIStatus,
} from '_redux/slices/sla-reports/sla-reports';

import { selectFirebaseInfo, selectIata } from "_redux/slices/main-screen/user";

// custom components
import CustomReportsTable from './custom-reports-table';
import { TabSwitcher, CustomSpinner } from '_atoms';
import ReportPageHeader from './report-page-header';
import PassengerStatCards from './passenger-stat-cards';
import AssistanceBreakdownCard from './assistance-breakdown-card';
import TimelineReportDashboardFilter from './timeline-report-dashboard-filter';
import TimelineReportFooter from './timeline-report-footer';
import SlaReport from './sla-report';
import AtLocReCheckPanel from './AtLocReCheckPanel';
import TaskAtLocCheckButton from './TaskAtLocCheckButton';
import { Spacing } from '_styles';

//date
import moment from 'moment';

//#endregion

// report list to be shown
const reports = [
    {
        label: 'Passenger Breakdown',
        value: 'passenger-breakdown',
        description: 'Comprehensive passenger assistance history and records',
        roles: []
    },
    {
        label: 'Allocation Report',
        value: 'passenger-records',
        description: 'Passenger journey records and assistance history',
        roles: []

    },
    {
        label: 'SLA Report',
        value: 'sla-reports',
        description: 'SLA Reports for passenger assistance',
        roles: ["REPORT"],
        airports: ["SNN", "PUJ"]

    }
]


const TimelineReportDashboard = () => {

    // controller for aborting in-flight report requests (held across renders)
    const controllerRef = useRef(null);

    //#region redux + states
    const dispatch = useDispatch();

    // iata redux
    const iata = useSelector(selectIata);           // iata
    const dbUser = useSelector(selectFirebaseInfo);                                        // logged in user

    // date
    const currentDate = new Date()
    currentDate.setMinutes(currentDate.getMinutes() + 30)
    currentDate.setMinutes(0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const [dateRange, setDateRange] = useState([
        today,
        currentDate
    ]);

    // Reports visible to current user: no roles/airports = everyone;
    // otherwise visible if user position is in roles OR user's airport is in airports
    const userPosition = dbUser?.position;
    const userRole = dbUser?.role;

    const visibleReports = userRole === "REPORTS_ONLY"
        ? reports.filter((r) => r.value === 'sla-reports')
        : reports.filter(
            (r) =>
                (!r.roles?.length && !r.airports?.length) ||
                (userPosition && r.roles?.includes(userPosition)) ||
                (iata && r.airports?.includes(iata))
        );

    // selected SLA report within sla-reports (movement, flight-audit, etc.)
    const [selectedSlaReport, setSelectedSlaReport] = useState('ecac-departure-summary');

    // selected report type
    const selectedReportType = useSelector(selectSelectedReportType) || 'passenger-breakdown';

    // When switching to ECAC, Boarding SLA Report, etc., coerce date range to single day
    useEffect(() => {
        const singleDateReports = [];
        if (selectedReportType === 'sla-reports' && singleDateReports.includes(selectedSlaReport) && dateRange?.[0]) {
            const start = dateRange[0];
            const end = dateRange[1];
            const startDay = new Date(start).toDateString();
            const endDay = end ? new Date(end).toDateString() : startDay;
            if (startDay !== endDay) {
                setDateRange([start, start]);
            }
        }
    }, [selectedReportType, selectedSlaReport]);

    // If current tab is not in visible reports (e.g. SLA when user is not REPORT), switch to first visible
    useEffect(() => {
        const isCurrentTabVisible = visibleReports.some((r) => r.value === selectedReportType);
        if (!isCurrentTabVisible && visibleReports.length > 0) {
            dispatch(setSelectedReportType(visibleReports[0].value));
        }
    }, [visibleReports, selectedReportType, dispatch]);

    // include no shows toggle from state
    const includeNoShows = useSelector(selectIncludeNoShows);

    // departure/arrival toggle value from state
    const selectedDepartureArrival = useSelector(selectSelectedDepartureArrival);

    // passenger records report   
    const passengerRecordsReportDataAndColumns = useSelector((state) => selectPassengerRecordsReportDataAndColumns(state, iata));
    const passengerRecordStatus = useSelector(selectPassengerRecordsReportStatus);

    // passenger breakdown
    const passengerBreakdownStatus = useSelector(selectPassengerBreakdownStatus);
    const passengerBreakdownDataAndColumns = useSelector(selectPassengerBreakdownDataAndColumns);

    // sla report
    const slaReportStatus = useSelector(selectSLAReportStatus);
    const externalKPIStatus = useSelector(selectExternalKPIStatus);

    const showPaxJourney = useSelector(selectShowPaxJourney);

    const [showReCheck, setShowReCheck] = useState(false);

    // search query for the passenger records (allocation) report
    const [recordsSearchQuery, setRecordsSearchQuery] = useState('');

    //#endregion

    //#region styles
    const containerStyle = {
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
    };

    const tabsListStyle = {
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 8px',
    };

    const getTabTriggerStyle = (isActive) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px 12px',
        fontSize: '13px',
        fontWeight: isActive ? '600' : '500',
        color: isActive ? '#3b82f6' : '#6b7280',
        backgroundColor: isActive ? '#eff6ff' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        whiteSpace: 'nowrap',
        flex: '1',
        minWidth: '0',
        borderRadius: '8px 8px 0 0',
        margin: '8px 4px 0 4px',
        position: 'relative',
        borderBottom: isActive ? '3px solid #3b82f6' : '3px solid transparent'
    });

    const iconStyle = {
        fontSize: '20px',
        marginBottom: '6px',
        display: 'block'
    };

    const labelStyle = {
        fontSize: '12px',
        fontWeight: 'inherit',
        textAlign: 'center',
        lineHeight: '1.2'
    };

    const tabContentStyle = {
        padding: '24px',
        backgroundColor: '#ffffff',
        minHeight: '400px'
    };

    //#endregion

    //#region functions
    // Handle tab change
    const handleTabChange = (value) => {
        // set selected report type
        dispatch(setSelectedReportType(value));
        dispatch(setShowPaxJourney(false));
    };

    // function to get passenger records report
    const getPassengerRecordsReportData = useCallback(async () => {
        // Use the current dateRange state for the API call
        const currentStartDate = dateRange[0];
        const currentEndDate = dateRange[1];
        if (!currentEndDate || !currentStartDate) return

        const formattedStartDate = moment(new Date(currentStartDate)).format().split("T")[0];
        const formattedEndDate = moment(new Date(currentEndDate)).format().split("T")[0];

        const payload = {
            iata: iata,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            type: selectedDepartureArrival === 'Departures' ? 'outbound' : selectedDepartureArrival === 'Arrivals' ? 'inbound' : 'all',
            // only fetch expensive at-location journey details when the journey view is on
            includeJourneyDetails: showPaxJourney,
            // location history is cheap (no extra reads) so always request it — the
            // location icon works regardless of the passenger-journey view toggle
            includeLocationHistory: true,
        };

        controllerRef.current?.abort();
        controllerRef.current = new AbortController();
        dispatch(getPassengerRecordsReport(payload, controllerRef.current.signal));
    }, [dateRange, selectedDepartureArrival, includeNoShows, iata, dispatch, showPaxJourney]);

    const getPassengerBreakdownData = useCallback(async () => {

        const currentStartDate = dateRange[0];
        const currentEndDate = dateRange[1];

        if (!currentEndDate || !currentStartDate) return

        const formattedStartDate = moment(new Date(currentStartDate)).format().split("T")[0];
        const formattedEndDate = moment(new Date(currentEndDate)).format().split("T")[0];

        const payload = {
            iata: iata,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            type: selectedDepartureArrival === 'Departures' ? 'outbound' : selectedDepartureArrival === 'Arrivals' ? 'inbound' : 'all',
            includeNoShows: includeNoShows,
        };

        //console.log('getPassengerBreakdownData - payload:', payload);
        controllerRef.current?.abort();
        controllerRef.current = new AbortController();
        dispatch(getPassengerBreakdown(payload, controllerRef.current.signal));
    }, [dateRange, selectedDepartureArrival, includeNoShows, iata, dispatch]);

    //#endregion

    //#region use effects
    useEffect(() => {

        if (selectedReportType == 'passenger-breakdown') {
            getPassengerBreakdownData();
        }
        else if (selectedReportType == 'passenger-records') {
            getPassengerRecordsReportData();
        }
        else if (selectedReportType == 'sla-reports') {
            //getSLAReportData();
        }
    }, [selectedReportType, dateRange, selectedDepartureArrival, includeNoShows, showPaxJourney]);

    // abort any in-flight report request on unmount
    useEffect(() => {
        return () => {
            controllerRef.current?.abort();
        };
    }, []);

    //#endregion

    const isLoading = (
        (selectedReportType == 'passenger-breakdown' && passengerBreakdownStatus == 'loading') ||
        (selectedReportType == 'passenger-records' && passengerRecordStatus == 'loading') ||
        (selectedReportType == 'sla-reports' && (slaReportStatus == 'loading' || externalKPIStatus == 'loading'))
    );

    // filter the passenger records data by passenger name or flight number
    const filteredRecords = useMemo(() => {
        const { columns = [], data = [] } = passengerRecordsReportDataAndColumns || {};
        const q = recordsSearchQuery.trim().toLowerCase();
        if (!q) return { columns, data };
        const nameIdx = columns.findIndex((c) => c.key === 'name');
        const flightIdx = columns.findIndex((c) => c.key === 'flightNumber');
        const filteredData = data.filter((row) => {
            const name = String(row?.[nameIdx] ?? '').toLowerCase();
            const flight = String(row?.[flightIdx] ?? '').toLowerCase();
            return name.includes(q) || flight.includes(q);
        });
        return { columns, data: filteredData };
    }, [passengerRecordsReportDataAndColumns, recordsSearchQuery]);

    return (
        <>
            <div style={containerStyle}>

                {/* tab switcher */}
                <TabSwitcher
                    tabs={visibleReports}
                    activeTab={selectedReportType}
                    onTabChange={handleTabChange}
                    containerStyle={{}}
                    tabsListStyle={{ ...tabsListStyle, gridTemplateColumns: `repeat(${visibleReports.length}, 1fr)` }}
                    getTabTriggerStyle={getTabTriggerStyle}
                    iconStyle={iconStyle}
                    labelStyle={labelStyle}
                    tabContentStyle={tabContentStyle}
                >

                    <div>

                        {/* SLA Report - always mounted to preserve report data when switching tabs */}
                        <div style={{
                            display: selectedReportType === 'sla-reports' ? 'block' : 'none',
                            padding: Spacing.SCALE_12
                        }}>
                            <div>
                                {isLoading && selectedReportType === 'sla-reports' ? (
                                    <CustomSpinner rowCenter={true} />
                                ) : (
                                    <SlaReport dateRange={dateRange} setDateRange={setDateRange} iata={iata} onReportChange={setSelectedSlaReport} />
                                )}
                            </div>
                        </div>

                        {/* Passenger Breakdown / Allocation Records - always mounted to preserve data when switching tabs */}
                        <div style={{
                            display: (selectedReportType === 'passenger-breakdown' || selectedReportType === 'passenger-records') ? 'block' : 'none',
                            padding: Spacing.SCALE_12
                        }}>
                            <ReportPageHeader
                                title={
                                    selectedReportType == 'passenger-breakdown' ? '' :
                                        selectedReportType == 'passenger-records' ? 'Allocation Records' :
                                            ''}
                                description={
                                    selectedReportType == 'passenger-breakdown' ? '' :
                                        selectedReportType == 'passenger-records' ? 'Detailed passenger journey records and assistance history' :
                                            ''}
                            />
                            <TimelineReportDashboardFilter
                                dateRange={dateRange}
                                setDateRange={(val) => { setDateRange(val) }}
                                singleDateOnly={false}
                                searchQuery={recordsSearchQuery}
                                onSearchChange={(e) => setRecordsSearchQuery(e.target.value)}
                            />
                            <hr style={{ border: 'solid', borderTop: '1px solidrgb(54, 109, 219)', margin: '22px 0', borderWidth: 1 }} />
                            <div>
                                {isLoading ? (
                                    <CustomSpinner rowCenter={true} />
                                ) : (
                                    <div>
                                        {/* total assistance breakdown card (multi-day passenger breakdown only) */}
                                        {selectedReportType == 'passenger-breakdown' && <AssistanceBreakdownCard />}

                                        {/* stat cards */}
                                        <PassengerStatCards />

                                        {/* data table */}
                                        <div className='mt-3'>
                                        <CustomReportsTable
                                            columns={
                                                selectedReportType == 'passenger-breakdown' ? passengerBreakdownDataAndColumns?.columns :
                                                    selectedReportType == 'passenger-records' ? filteredRecords.columns :
                                                        []
                                            }
                                            data={
                                                selectedReportType == 'passenger-breakdown' ? passengerBreakdownDataAndColumns?.data :
                                                    selectedReportType == 'passenger-records' ? filteredRecords.data :
                                                        []
                                            }
                                            showChildrenData={showPaxJourney}
                                            showFlightExpand={selectedReportType === 'passenger-records'}
                                            // renderAtLocCheckButton={
                                            //     selectedReportType === 'passenger-records' && showPaxJourney
                                            //         ? (meta) => (
                                            //             meta?.taskId && meta?.pickUpLocationId && !meta?.isVerified
                                            //                 ? <TaskAtLocCheckButton taskId={meta.taskId} iata={iata} />
                                            //                 : null
                                            //         )
                                            //         : undefined
                                            // }
                                        />
                                        </div>

                                        {/* dashboard footer */}
                                        <TimelineReportFooter
                                            passengersData={selectedReportType == 'passenger-breakdown' ? passengerBreakdownDataAndColumns : passengerRecordsReportDataAndColumns}
                                            iata={iata}
                                            dateRange={dateRange}
                                            statusLoading={selectedReportType == 'passenger-breakdown' ? passengerBreakdownStatus == 'loading' : passengerRecordStatus == 'loading'}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>


                </TabSwitcher>
            </div>
        </>
    );
};

export default TimelineReportDashboard;
